import { Navigate, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  ContentCard,
  Row,
  Button,
  TextInput,
  Tooltip,
} from "@narmi/design_system";
import { cents, centsToDollars } from "byzantine";
import Filters from "byzantine/src/filters";
import { MAX_BULK_TITLE_LENGTH } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { MAX_MEMO_LENGTH } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import HeaderWithSteps from "../../HeaderWithSteps";
import Container from "../../Container";
import { useACHPassthroughImportContext } from "../ACHPassthroughImportContext";
import styles from "./PassthroughAdditionalDetailsScreen.module.scss";

const formatCurrencyFromCents = (amountCents: number): string =>
  Filters.currency(centsToDollars(cents(amountCents)), { hasDecimal: true });

const PassthroughAdditionalDetailsScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const {
    validateResponse,
    fileName,
    title,
    internalNote,
    setTitle,
    setInternalNote,
    clearValidation,
  } = useACHPassthroughImportContext();

  if (!validateResponse || !validateResponse.file_summary) {
    return <Navigate to="../.." replace />;
  }

  const { file_summary: fileSummary } = validateResponse;
  const netAmountCents =
    fileSummary.payment_total_debit_amount - fileSummary.payment_total_credit_amount;

  const handleCancel = () => {
    clearValidation();
    navigate("/transfer");
  };

  const handleNext = () => {
    navigate("../review");
  };

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps
        useDisplayFont
        headerText={l10n.getString("ach-passthrough-additional-details-title")}
      />
      <div className="margin--top--l">
        <ContentCard kind="bordered" paddingSize="l" radiusSize="m">
          <div className={styles.fileSummaryContent}>
            <div className={styles.fileSummaryLeft}>
              <span className="narmi-icon-file-text1" />
              <div className={styles.fileSummaryText}>
                <div className={`${styles.fileName} fontWeight--semibold`}>
                  {fileName}
                </div>
                <div className="fontSize--s fontColor--secondary">
                  {l10n.getString(
                    "ach-passthrough-additional-details-payment-summary",
                    {
                      count:
                        fileSummary.payment_credit_count +
                        fileSummary.payment_debit_count,
                      amount: formatCurrencyFromCents(netAmountCents),
                    }
                  )}
                </div>
              </div>
            </div>
            <div className={styles.fileSummaryRight}>
              <div className={styles.fileSummaryStat}>
                <div className="fontSize--xs fontColor--secondary">
                  {l10n.getString("ach-passthrough-validation-send-count", {
                    count: fileSummary.payment_credit_count,
                  })}
                </div>
                <div className="fontWeight--semibold">
                  {formatCurrencyFromCents(fileSummary.payment_total_credit_amount)}
                </div>
              </div>
              <div className={styles.fileSummaryStat}>
                <div className="fontSize--xs fontColor--secondary">
                  {l10n.getString("ach-passthrough-validation-collect-count", {
                    count: fileSummary.payment_debit_count,
                  })}
                </div>
                <div className="fontWeight--semibold">
                  {formatCurrencyFromCents(fileSummary.payment_total_debit_amount)}
                </div>
              </div>
            </div>
          </div>
        </ContentCard>
      </div>

      <div className="margin--top--l">
        <Row>
          <Row.Item>
            <TextInput
              label={l10n.getString("bulk-payment-title")}
              maxLength={MAX_BULK_TITLE_LENGTH}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              endContent={
                <Tooltip
                  text={l10n.getString("bulk-payment-title-tooltip-copy")}
                  maxWidth="235px"
                >
                  <span className="narmi-icon-info"></span>
                </Tooltip>
              }
            />
          </Row.Item>
        </Row>
      </div>

      <div className="margin--top--l">
        <Row>
          <Row.Item>
            <TextInput
              label={l10n.getString("label-internal-note-optional")}
              maxLength={MAX_MEMO_LENGTH}
              value={internalNote}
              onChange={(event) => setInternalNote(event.target.value)}
              endContent={
                <Tooltip
                  text={l10n.getString(
                    "ach-passthrough-additional-details-note-tooltip"
                  )}
                  maxWidth="235px"
                >
                  <span className="narmi-icon-info"></span>
                </Tooltip>
              }
            />
          </Row.Item>
        </Row>
      </div>

      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("button-cancel")}
              onClick={handleCancel}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleNext}
            />
          </Row.Item>
        </Row>
      </div>
    </Container>
  );
};

export default PassthroughAdditionalDetailsScreen;
